import { motion } from "framer-motion";

const steps = [
  {
    title: "Requirement Analysis",
    desc: "We start by understanding your product, load specs, and supply chain needs.",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
  },
  {
    title: "Packaging Design",
    desc: "Our in-house team creates structural designs and prototypes for your approval.",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
      </svg>
    ),
  },
  {
    title: "Material Selection",
    desc: "Board grade, flute type, and GSM are chosen to match strength and cost targets.",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
  },
  {
    title: "Manufacturing",
    desc: "Precision cutting, creasing, and gluing on modern corrugating machinery.",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    title: "QC & Delivery",
    desc: "Every batch is inspected before dispatch to ensure it arrives perfect.",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1], delay },
});

export default function Process() {
  return (
    <section id="process" className="py-28 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <motion.div {...fadeUp(0)} className="text-center mb-20">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block w-8 h-px bg-brand-green" />
            <span className="text-brand-green text-xs uppercase tracking-[0.2em] font-semibold">How We Work</span>
            <span className="block w-8 h-px bg-brand-green" />
          </div>
          <h2 className="font-heading text-4xl md:text-5xl mb-4">Our Manufacturing Process</h2>
          <p className="text-gray-500 max-w-xl mx-auto text-base leading-relaxed">
            A structured approach ensuring strength, consistency, and performance
            at every stage of production.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">

          {/* Connector line (desktop) */}
          <div className="hidden md:block absolute top-10 left-[10%] right-[10%] h-px bg-gray-200 z-0" />

          {/* Animated green fill on connector */}
          <motion.div
            className="hidden md:block absolute top-10 left-[10%] h-px bg-brand-green z-0 origin-left"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: "easeInOut", delay: 0.3 }}
            style={{ right: "10%" }}
          />

          <div className="grid md:grid-cols-5 gap-8 relative z-10">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                {...fadeUp(0.1 + index * 0.1)}
                className="flex flex-col items-center text-center group"
              >
                {/* Circle with icon */}
                <div className="relative mb-6">
                  {/* Step number badge */}
                  <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-brand-green text-white text-[10px] font-bold flex items-center justify-center z-10 shadow-sm">
                    {index + 1}
                  </div>

                  <div className="w-20 h-20 rounded-2xl bg-white border-2 border-gray-100 group-hover:border-brand-green text-gray-400 group-hover:text-brand-green flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-300">
                    {step.icon}
                  </div>
                </div>

                <h4 className="font-heading text-base font-semibold mb-2 leading-snug text-gray-800">
                  {step.title}
                </h4>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}